import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { auth } from '../supabaseClient';
import PageSeo from '../components/PageSeo';
export default function Login() {
  const { user, login } = useApp();
  const navigate = useNavigate();
  const [mode, setMode] = useState('signin');
  const [accountType, setAccountType] = useState('rider');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [bike, setBike] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSignIn = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Enter your email address.');
      return;
    }

    setSuccessMessage('');
    if (password.length < 6) {
      setError('Enter your password to sign in.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const { data, error: signInError } = await auth.signInWithEmail(email.trim(), password);
      if (signInError) {
        setError(signInError.message);
        return;
      }

      login({ email: email.trim() }, data?.user);
      navigate('/');
    } catch (err) {
      setError(err?.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Enter your full name.');
      return;
    }

    setSuccessMessage('');
    if (!email.trim()) {
      setError('Enter your email address.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const trimmedName = name.trim();
      const trimmedEmail = email.trim();
      const trimmedCity = city.trim();
      const trimmedBike = bike.trim();
      const formattedPhone = phone.trim() ? `+91${phone.trim()}` : null;

      const { data, error: signUpError } = await auth.signUpWithEmail(trimmedEmail, password, {
        name: trimmedName,
        city: trimmedCity,
        bike: trimmedBike,
        role: accountType,
      });

      if (signUpError) {
        setError(signUpError.message);
        setLoading(false);
        return;
      }

      if (!data?.session) {
        setSuccessMessage('Verification email sent. Click the link in your email. After verification you will return here and be signed in automatically.');
        setLoading(false);
        return;
      }

      const success = await login({
        name: trimmedName,
        email: trimmedEmail,
        phone: formattedPhone,
        city: trimmedCity,
        bike: trimmedBike,
        role: accountType,
      }, data?.user);

      if (success) {
        navigate('/');
      } else {
        setError('Account created, but profile setup was incomplete. Please sign in and try again.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, position: 'relative', overflow: 'hidden'
    }}>
      <PageSeo
        title="Login | MotoHub India Rider Account"
        description="Sign in to your MotoHub India rider account to join bike rides, access community chat, and manage your motorcycle profile."
        keywords={["MotoHub India login", "rider login", "motorcycle community account"]}
        path="/login"
        noindex
      />
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.03,
        backgroundImage: 'repeating-linear-gradient(45deg, #E84C1E 0, #E84C1E 1px, transparent 0, transparent 50%)',
        backgroundSize: '30px 30px'
      }} />

      <div className="fade-up" style={{ width: '100%', maxWidth: 420, position: 'relative' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 52, letterSpacing: 2, color: 'var(--brand)', lineHeight: 1 }}>MOTO</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 52, letterSpacing: 2, color: 'var(--text)', lineHeight: 1 }}>BHARAT</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 8, letterSpacing: 2, textTransform: 'uppercase' }}>India's Biker Community</div>
        </div>

        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: 28 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            <button
              type="button"
              onClick={() => {
                setMode('signin');
                setError('');
                setSuccessMessage('');
              }}
              style={{
                ...switchBtnStyle,
                background: mode === 'signin' ? 'var(--brand)' : 'var(--bg3)',
                color: mode === 'signin' ? '#fff' : 'var(--text2)',
                border: `1px solid ${mode === 'signin' ? 'var(--brand)' : 'var(--border2)'}`,
              }}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('signup');
                setError('');
                setSuccessMessage('');
              }}
              style={{
                ...switchBtnStyle,
                background: mode === 'signup' ? 'var(--brand)' : 'var(--bg3)',
                color: mode === 'signup' ? '#fff' : 'var(--text2)',
                border: `1px solid ${mode === 'signup' ? 'var(--brand)' : 'var(--border2)'}`,
              }}
            >
              Sign Up
            </button>
          </div>

          {mode === 'signin' ? (
            <form onSubmit={handleSignIn}>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>Sign in to your account</div>
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>Use the email and password you registered with</div>
              </div>
              <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} style={{ marginBottom: 14 }} />
              <div style={passwordFieldWrapStyle}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={passwordInputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  style={toggleBtnStyle}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 10 }}>{error}</div>}
              <button type="submit" style={btnStyle} disabled={loading}>
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
              {successMessage && <div style={successTextStyle}>{successMessage}</div>}
              <div style={helperTextStyle}>
                New user?{' '}
                <button type="button" onClick={() => { setMode('signup'); setError(''); setSuccessMessage(''); }} style={textLinkStyle}>
                  Create account
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSignUp}>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>Create your account</div>
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>Sign up first, then log in with the same credentials</div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button
                  type="button"
                  onClick={() => setAccountType('rider')}
                  style={{
                    ...switchBtnStyle,
                    background: accountType === 'rider' ? 'var(--brand)' : 'var(--bg3)',
                    color: accountType === 'rider' ? '#fff' : 'var(--text2)',
                    border: `1px solid ${accountType === 'rider' ? 'var(--brand)' : 'var(--border2)'}`,
                  }}
                >
                  Rider
                </button>
                <button
                  type="button"
                  onClick={() => setAccountType('mechanic')}
                  style={{
                    ...switchBtnStyle,
                    background: accountType === 'mechanic' ? 'var(--brand)' : 'var(--bg3)',
                    color: accountType === 'mechanic' ? '#fff' : 'var(--text2)',
                    border: `1px solid ${accountType === 'mechanic' ? 'var(--brand)' : 'var(--border2)'}`,
                  }}
                >
                  Mechanic
                </button>
              </div>
              {accountType === 'mechanic' ? (
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.6 }}>
                  Mechanic accounts can manage their own garage listing anytime from the Mechanic Finder page.
                </div>
              ) : null}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                <input placeholder="Full name *" value={name} onChange={e => setName(e.target.value)} />
                <input type="email" placeholder="Email address *" value={email} onChange={e => setEmail(e.target.value)} />
                <input type="tel" placeholder="Phone number (optional)" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g,'').slice(0,10))} maxLength={10} />
                <input placeholder="City (e.g. Pune, Mumbai)" value={city} onChange={e => setCity(e.target.value)} />
                <input placeholder="Your bike (e.g. RE Himalayan 450)" value={bike} onChange={e => setBike(e.target.value)} />
                <div style={passwordFieldWrapStyle}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    style={passwordInputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    style={toggleBtnStyle}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div style={passwordFieldWrapStyle}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    style={passwordInputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(prev => !prev)}
                    style={toggleBtnStyle}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 10 }}>{error}</div>}
              <button type="submit" style={btnStyle} disabled={loading}>
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>
              {successMessage && <div style={successTextStyle}>{successMessage}</div>}
              <div style={helperTextStyle}>
                Already registered?{' '}
                <button type="button" onClick={() => { setMode('signin'); setError(''); setSuccessMessage(''); }} style={textLinkStyle}>
                  Sign in here
                </button>
              </div>
            </form>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: 'var(--text3)' }}>
          By joining, you agree to ride responsibly.
        </div>
      </div>
    </div>
  );
}

const btnStyle = {
  width: '100%', padding: '12px', borderRadius: 'var(--radius)',
  background: 'var(--brand)', color: '#fff', fontSize: 15, fontWeight: 600,
  letterSpacing: 0.3
};

const switchBtnStyle = {
  flex: 1,
  padding: '10px 14px',
  borderRadius: 'var(--radius)',
  fontSize: 14,
  fontWeight: 600,
};

const helperTextStyle = {
  textAlign: 'center',
  marginTop: 14,
  fontSize: 12,
  color: 'var(--text2)',
};

const textLinkStyle = {
  background: 'none',
  color: 'var(--brand)',
  padding: 0,
  fontSize: 12,
  fontWeight: 600,
};

const passwordFieldWrapStyle = {
  position: 'relative',
  marginBottom: 14,
};

const passwordInputStyle = {
  paddingRight: '72px',
};

const toggleBtnStyle = {
  position: 'absolute',
  right: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  color: 'var(--brand)',
  fontSize: 12,
  fontWeight: 700,
  padding: 0,
};

const successTextStyle = {
  fontSize: 12,
  color: 'var(--success)',
  marginTop: 12,
  lineHeight: 1.6,
};
